import { apiConfigList } from '@/api'
import type { ConstDataListItem } from '@/types'
import { isArray } from 'lodash-es'

let loadingPromise: Promise<ConstDataListItem[]> | null = null
let constConfigList: ConstDataListItem[] = []

/**
 * 常量配置管理 V2 - 异步版本
 */
export const useConstConfigV2 = () => {
  const _loadConfig = async () => {
    if (constConfigList.length > 0) {
      return constConfigList
    }

    if (loadingPromise) {
      return loadingPromise
    }

    loadingPromise = Promise.all([
      apiConfigList(),
      new Promise((resolve) => setTimeout(resolve, 10))
    ])
      .then(([data]) => {
        if (isArray(data) && data.length > 0) {
          constConfigList = data
        }
        return constConfigList
      })
      .catch((error) => {
        console.error('加载配置失败:', error)
        return []
      })
      .finally(() => {
        loadingPromise = null
      })

    return loadingPromise
  }

  return {
    constConfigList,

    async getIsSmsNeedCaptcha() {
      await _loadConfig()
      // APP_REGISTER_SMS_CODE: 0 关闭，1 开启（需要短信验证码）
      const config = constConfigList.find(
        (it: ConstDataListItem) => it.configKey === 'APP_REGISTER_SMS_CODE'
      )
      return config?.value === '1'
    },

    async getSupportLangs() {
      await _loadConfig()
      const list = constConfigList.filter((it: ConstDataListItem) => it.category === 'APP_LANG')
      return {
        status: list.find((it) => it.configKey === 'APP_MULTI_LANGUAGE_STATUS')?.value === '1',
        'zh-CN': list.find((it) => it.configKey === 'APP_MULTI_LANGUAGE_CHINESE')?.value === '1',
        'en-US': list.find((it) => it.configKey === 'APP_MULTI_LANGUAGE_ENGLISH')?.value === '1',
        'vi-VN': list.find((it) => it.configKey === 'APP_MULTI_LANGUAGE_VIETNAMESE')?.value === '1',
        'hi-IN': list.find((it) => it.configKey === 'APP_MULTI_LANGUAGE_HINDI')?.value === '1',
        'ta-IN': list.find((it) => it.configKey === 'APP_MULTI_LANGUAGE_TAMIL')?.value === '1',
        'ms-MY': list.find((it) => it.configKey === 'APP_MULTI_LANGUAGE_MALAY')?.value === '1',
        'pt-BR': list.find((it) => it.configKey === 'APP_MULTI_LANGUAGE_PORTUGUESE')?.value === '1'
      }
    },

    // vip页面 活动规则
    async getVipInfos(locale: string, keyName: string) {
      await _loadConfig()
      const list = constConfigList.filter((it: ConstDataListItem) => it.category === 'MEMBER_VIP')

      const localeMap: Record<string, string> = {
        'en-US': 'EN',
        'vi-VN': 'VI',
        'zh-CN': 'ZH',
        'hi-IN': 'HI',
        'ta-IN': 'TA',
        'ms-MY': 'MS',
        'pt-BR': 'PT'
      }
      const langCode = localeMap[locale] || ''

      if (keyName === 'VIP_CARD_TIPS') {
        // "VIP卡片上的温馨提示"
        const configKeyName = langCode ? `MEMBER_VIP_TIPS_CONFIG_${langCode}` : ''
        return list.find((it) => it.configKey === configKeyName)?.value
      }
      if (keyName === 'VIP_DESCRIPTION') {
        // "VIP 页面说明"
        const configKeyName = langCode ? `MEMBER_VIP_ILLUSTRATE_CONFIG_${langCode}` : ''
        return list.find((it) => it.configKey === configKeyName)?.value
      }
      if (keyName === 'VIP_SHAREHOLDER_DESCRIPTION') {
        // VIP 股东分红页面说明
        const configKeyName = langCode ? `MEMBER_VIP_SHAREHOLDER_CONFIG_${langCode}` : ''
        return list.find((it) => it.configKey === configKeyName)?.value
      }
      // vip模块开关
      if (keyName === 'VIP_MODULE_SWITCH') {
        return list.find((it) => it.configKey === keyName)?.value
      }
      // vip返水模块开关
      if (keyName === 'REBATE_VIP_SWITCH') {
        return list.find((it) => it.configKey === keyName)?.value
      }
    },

    // vip返水模块开关
    async getRebate() {
      await _loadConfig()
      const list = constConfigList.filter(
        (it: ConstDataListItem) => it.category === 'REPORT_REBATE'
      )
      return list.find((it) => it.configKey === 'REBATE_VIP_SWITCH')?.value
    },

    // 图片资源的http前缀
    getS3Domain() {
      return import.meta.env.VITE_S3_URL as string
    },

    // 搜索页 游戏标签  使用二级游戏页标签
    async getSearchPageGameTags(t: (key: string) => string) {
      await _loadConfig()
      const labelMap: Record<string, string> = {
        热门: t('热门'),
        人气: t('人气'),
        推荐: t('推荐')
      }
      return constConfigList
        .filter((item: ConstDataListItem) => item.category === 'GAME_SEARCH_TYPE')
        .map((it: ConstDataListItem) => ({
          ...it,
          value: labelMap[it.value ?? ''] ?? it.value ?? ''
        }))
    },

    // 二级游戏页  游戏标签
    async getGamesPageTags(t: (key: string) => string) {
      await _loadConfig()
      const labelMap: Record<string, string> = {
        热门: t('热门'),
        人气: t('人气'),
        推荐: t('推荐')
      }
      const list = constConfigList
        .filter((item: ConstDataListItem) => item.category === 'SECOND_GAME_TAG')
        .filter((it: ConstDataListItem) => Boolean(it.configKey))
      return list.map((it: ConstDataListItem) => ({
        ...it,
        value: labelMap[it.value ?? ''] ?? it.value ?? ''
      }))
    },

    // 存款页配置
    async getDepositParams() {
      await _loadConfig()
      const list = constConfigList.filter((it: ConstDataListItem) => it.category === 'APP_MEMBER')
      return {
        // 是否检查手机号
        checkPhoneBind:
          list.find((item: ConstDataListItem) => item.configKey === 'APP_DEPOSIT_BIND_PHONE')
            ?.value === '1'
      }
    },

    // 账户安全页 姓名 手机号 等 是否可编辑
    async getAccountSecurityConfig() {
      await _loadConfig()
      const list = constConfigList.filter((it: ConstDataListItem) => it.category === 'APP_MEMBER')
      return {
        // 能否编辑手机号
        canEditPhone:
          list.find((it: ConstDataListItem) => it.configKey === 'APP_UPDATE_PHONE')?.value === '1',
        // 能否编辑真实姓名
        canEditName:
          list.find((it: ConstDataListItem) => it.configKey === 'APP_UPDATE_NAME')?.value === '1'
      }
    },

    // 首页底部5个联系方式
    async getHomePageContacts() {
      await _loadConfig()
      const list = constConfigList.filter((it: ConstDataListItem) => it.category === 'APP_HOMEPAGE')
      // 先判断此功能是否开启
      const enable =
        list.find((it: ConstDataListItem) => it.configKey === 'APP_CONTACT_INFO_STATUS')?.value ===
        '1'
      if (!enable) return []
      const find = list.find((it: ConstDataListItem) => it.configKey === 'APP_CONTACT_INFO_LIST')
      return (
        JSON.parse(find?.value || '[]') as {
          pic?: string
          url?: string
          name?: string
        }[]
      ).filter((it) => it.pic)
    },

    // 首页底部富文本描述
    async getHomePageRichText(locale: string) {
      await _loadConfig()
      const list = constConfigList.filter(
        (it: ConstDataListItem) => it.category === 'APP_HOMEPAGE_TEXT'
      )
      // 先判断此功能是否开启
      const enable =
        list.find((it: ConstDataListItem) => it.configKey === 'APP_HOMEPAGE_TEXT_STATUS')?.value ===
        '1'
      if (!enable) return undefined
      const localeMap: Record<string, string> = {
        'en-US': 'APP_HOMEPAGE_TEXT_EN',
        'vi-VN': 'APP_HOMEPAGE_TEXT_VI',
        'zh-CN': 'APP_HOMEPAGE_TEXT_ZH',
        'hi-IN': 'APP_HOMEPAGE_TEXT_HI',
        'ta-IN': 'APP_HOMEPAGE_TEXT_TA',
        'ms-MY': 'APP_HOMEPAGE_TEXT_MS',
        'pt-BR': 'APP_HOMEPAGE_TEXT_PT'
      }
      const keyName = localeMap[locale] || ''
      return list.find((it: ConstDataListItem) => it.configKey === keyName)?.value
    },

    // 首页视频
    async getHomePageVideo() {
      await _loadConfig()
      const list = constConfigList.filter(
        (it: ConstDataListItem) => it.category === 'APP_HOMEPAGE' || it.category === 'INDEX_VIDEO'
      )
      // 先判断此功能是否开启
      const enable =
        list.find((it: ConstDataListItem) => it.configKey === 'INDEX_VIDEO_SWITCH')?.value === '1'
      if (!enable) return ''
      return list.find((it: ConstDataListItem) => it.configKey === 'APP_HOMEPAGE_BRAND_VIDEO')
        ?.value
    },

    // 首页视频下面的滚动品牌图
    async getHomePageSponsor() {
      await _loadConfig()
      const list = constConfigList.filter((it: ConstDataListItem) => it.category === 'APP_HOMEPAGE')
      // 先判断此功能是否开启
      const enable =
        list.find((it: ConstDataListItem) => it.configKey === 'APP_HOMEPAGE_BRAND_IMAGE_STATUS')
          ?.value === '1'
      if (!enable) return ''
      const find = list.find(
        (it: ConstDataListItem) => it.configKey === 'APP_HOMEPAGE_BRAND_IMAGE_LIST'
      )
      return find ? find.value || '' : ''
    },

    // 授权登录
    async getAuthLogin() {
      await _loadConfig()
      const config: { [key: string]: string } = {}
      constConfigList
        .filter((it: ConstDataListItem) => it.category === 'APP_SOCIAL' && it.value === '1')
        .forEach((it: ConstDataListItem) => {
          it.configKey && (config[it.configKey] = '1')
        })
      return config
    },

    // 注册配置 是否显示邀请码 手机号 等
    async getResisterConfig() {
      await _loadConfig()
      const list = constConfigList.filter((it: ConstDataListItem) => it.category === 'APP_REGISTER')

      const defaultCode = constConfigList.find(
        (it: ConstDataListItem) => it.configKey === 'PHONE_AREA_CODE'
      )?.value
      console.log('----defaultCode', defaultCode)
      return {
        // 展示手机号
        showPhoneNumber:
          list.find((it: ConstDataListItem) => it.configKey === 'APP_REGISTER_PHONE')?.value ===
          '1',
        // 展示邀请码
        showInviteCode:
          list.find((it: ConstDataListItem) => it.configKey === 'APP_REGISTER_INVITATION_CODE')
            ?.value === '1',
        // 展示真实姓名
        showRealName:
          list.find((it: ConstDataListItem) => it.configKey === 'APP_REGISTER_NAME')?.value === '1',
        // 返回默认手机区域号码
        defaultAreaCode: defaultCode ? defaultCode : '855'
      }
    },

    // 提款时是否需要输入密码 MEMBER_WITHDRAW_PASSWORD_INPUT 1为开启 0为关闭，未配置时默认开启
    async getIsWithdrawNeedPassword() {
      await _loadConfig()
      const config = constConfigList.find(
        (it: ConstDataListItem) => it.configKey === 'MEMBER_WITHDRAW_PASSWORD_INPUT'
      )
      return config?.value !== '0'
    },

    //注册页面用户协议
    async getAgreement(locale: string) {
      await _loadConfig()
      const localeMap: Record<string, string> = {
        'en-US': 'USER_SERVICE_AGREEMENT_EN',
        'vi-VN': 'USER_SERVICE_AGREEMENT_VI',
        'zh-CN': 'USER_SERVICE_AGREEMENT_ZH',
        'hi-IN': 'USER_SERVICE_AGREEMENT_HI',
        'ta-IN': 'USER_SERVICE_AGREEMENT_TA',
        'ms-MY': 'USER_SERVICE_AGREEMENT_MS',
        'pt-BR': 'USER_SERVICE_AGREEMENT_PT'
      }
      const keyName = localeMap[locale] || ''
      const config = constConfigList.find((it: ConstDataListItem) => it.configKey === keyName)
      return (config ? config.value : '') as string
    },

    // 关于我们
    async getAboutUs(locale: string) {
      console.log('locale,', locale)
      await _loadConfig()
      const localeMap: Record<string, string> = {
        'en-US': 'ABOUT_US_EN',
        'vi-VN': 'ABOUT_US_VI',
        'zh-CN': 'ABOUT_US_ZH',
        'hi-IN': 'ABOUT_US_HI',
        'ta-IN': 'ABOUT_US_TA',
        'ms-MY': 'ABOUT_US_MS',
        'pt-BR': 'ABOUT_US_PT'
      }
      const keyName = localeMap[locale] || ''
      const config = constConfigList.find((it: ConstDataListItem) => it.configKey === keyName)
      return (config ? config.value : '') as string
    },

    // 游戏卡片底部样式
    async getCardStyle() {
      await _loadConfig()
      const config = constConfigList.find(
        (it: ConstDataListItem) => it.configKey === 'GAME_COVER_BG'
      )
      return !(config?.value && config.value === '1')
    },

    // 隐藏游戏卡片底部
    async gameCardBottomShow() {
      await _loadConfig()
      const config = constConfigList.find(
        (it: ConstDataListItem) => it.configKey === 'GAME_BOTTOM_BG_TITLE'
      )
      return !(config?.value && config.value === '0')
    },

    // 首页tab配置
    async getHomeLayoutConfig() {
      await _loadConfig()
      // 娱乐城是否开启
      const isOpenEntertainment =
        constConfigList.find((it: ConstDataListItem) => it.configKey === 'APP_TAB_ENTERTAINMENT')
          ?.value === '1'
      return {
        home: true,
        entertainment:
          isOpenEntertainment &&
          constConfigList.find((it: ConstDataListItem) => it.configKey === 'ENTERTAINMENT_TYPE')
            ?.value === '1',
        // 未完成的--存提款需求
        wallet:
          isOpenEntertainment &&
          constConfigList.find((it: ConstDataListItem) => it.configKey === 'ENTERTAINMENT_TYPE')
            ?.value === '2',
        // 是否显示积分系统入口
        credit:
          constConfigList.find((it: ConstDataListItem) => it.configKey === 'ACT_POINT_SWITCH')
            ?.value === '1',
        discounts:
          constConfigList.find((it: ConstDataListItem) => it.configKey === 'APP_TAB_OFFERS')
            ?.value === '1',
        share:
          constConfigList.find((it: ConstDataListItem) => it.configKey === 'APP_TAB_MAKE_MONEY')
            ?.value === '1',
        mine:
          constConfigList.find((it: ConstDataListItem) => it.configKey === 'APP_TAB_MY_ACCOUNT')
            ?.value === '1'
      }
    },

    async getHomeNoticeConfig() {
      await _loadConfig()
      return (
        constConfigList.find((it: ConstDataListItem) => it.configKey === 'NOTICE_INDEX_REMINDER')
          ?.value === '1'
      )
    },

    // 后台返佣开关（1为开启，0为关闭）
    async getRebateSwitch() {
      await _loadConfig()
      return (
        constConfigList.find(
          (it: ConstDataListItem) => it.configKey === 'AGENT_BET_COMMISSION_SWITCH'
        )?.value === '1'
      )
    },

    // 个人中心，代理合作开关和图片路径地址
    async getMineAgencySwitch() {
      await _loadConfig()
      const isOpen =
        constConfigList.find((it: ConstDataListItem) => it.configKey === 'APP_AGENT_MODEL_SWITCH')
          ?.value === '1'
      const url = constConfigList.find(
        (it: ConstDataListItem) => it.configKey === 'APP_AGENT_MODEL_IMG_URL'
      )?.value

      return {
        status: isOpen,
        imageUrl: url
      }
    },

    // 分享配置
    async getShareConfig() {
      await _loadConfig()
      const list = constConfigList.filter((it: ConstDataListItem) => it.category === 'AGENT_SHARE')
      return {
        // 短信分享
        sms:
          list.find(
            (it: ConstDataListItem) => it.configKey === 'AGENT_SHARE_INVITE_TYPE_SMS_SWITCH'
          )?.value === '1',
        // 海报分享
        poster:
          list.find(
            (it: ConstDataListItem) => it.configKey === 'AGENT_SHARE_INVITE_TYPE_IMAGE_SWITCH'
          )?.value === '1',
        // 链接分享
        link:
          list.find(
            (it: ConstDataListItem) => it.configKey === 'AGENT_SHARE_INVITE_TYPE_URL_SWITCH'
          )?.value === '1',
        //推广步骤
        step:
          list.find((it: ConstDataListItem) => it.configKey === 'AGENT_SHARE_PROMOTE_STEP_SWITCH')
            ?.value === '1',
        // 推广平台
        platform:
          list.find(
            (it: ConstDataListItem) => it.configKey === 'AGENT_SHARE_PROMOTE_PLATFORM_SWITCH'
          )?.value === '1',
        // 推广奖励
        reward:
          list.find((it: ConstDataListItem) => it.configKey === 'AGENT_SHARE_PROMOTE_REWARD_SWITCH')
            ?.value === '1'
      }
    }
  }
}
